import { useEffect, useRef, useState } from 'react';
import { previewSetupSupplierFile } from '../../../../../services/accountSetup/supplierSetupService';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText } from '../../../../../styling/stylingHelper';
import {
  getFlaggedProductOrders,
  findGmailAccount,
  fetchOrderSourceFileText,
  sendIssueToCodeMonkey,
  type FlaggedProductWithRestaurant,
} from '../../../../../services/admin/adminService';
import { Copy, FileText, MoreVertical, Search, Terminal } from 'lucide-react';
import { buildQuantityCaseInfo } from '../shared/quantityPrompt';
import type { TFn } from '../types';

// Per-flag "⋮" overflow menu — collects the less-common row action (code-monkey handoff) into one kebab,
// mirroring the email-row menu. The dropdown is positioned with fixed coords captured from the button rect
// so it is not clipped by the table's horizontal-scroll container. "Send to code monkey" opens a Claude
// session seeded with the fix-quantities prompt.
export function QtyFlagRowMenu({ flag, t, isAuditTrace = false }: { flag: FlaggedProductWithRestaurant; t: TFn; isAuditTrace?: boolean }) {
  const [open, setOpen] = useState(false);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const busy = busyItem !== null;

  const close = () => { setOpen(false); setError(null); setDone(null); setBusyItem(null); };

  const toggle = () => {
    if (open) { close(); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const right = window.innerWidth - r.right;
      const ESTIMATED_H = 170;
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < ESTIMATED_H && r.top > spaceBelow;
      setPos(flipUp ? { bottom: window.innerHeight - r.top + 4, right } : { top: r.bottom + 4, right });
    }
    setOpen(true);
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Open the most recent stored invoice file for this flagged product directly (skips the detail modal).
  const handleOpenFile = async () => {
    if (busy) return;
    setBusyItem('file'); setError(null); setDone(null);
    try {
      if (flag.setupSource) {
        const { url } = await previewSetupSupplierFile(flag.setupSource.restaurantId, flag.setupSource.fileId);
        window.open(url, '_blank', 'noopener');
        close();
        return;
      }
      const orders = await getFlaggedProductOrders({
        platform: flag.platform,
        productName: flag.productName,
        articleId: flag.articleId,
        restaurantName: flag.restaurantName,
        gmailId: flag.gmailId,
      });
      const withFile = orders.find((o) => o.sourceFileUrl);
      if (withFile?.sourceFileUrl) {
        window.open(withFile.sourceFileUrl, '_blank', 'noopener');
        close();
      } else {
        setError(t('adminTesting.review.menuNoFile'));
      }
    } catch (err: any) {
      console.error('Failed to open invoice file for quantity flag', err);
      setError(err.message || 'Failed');
    } finally {
      setBusyItem(null);
    }
  };

  // Copy the SAME payload the code-monkey handoff sends (fix prompt + DB inspection + raw invoice
  // text) to the clipboard, for pasting into a local Claude session instead.
  const handleCopyFullPrompt = async () => {
    if (busy) return;
    setBusyItem('copy'); setError(null); setDone(null);
    try {
      let caseInfo = await buildQuantityCaseInfo(flag);
      if (flag.gmailId) {
        try {
          const { fileText } = await fetchOrderSourceFileText(flag.gmailId, flag.source ?? 'pakbon', flag.setupSource);
          if (fileText) caseInfo = `${caseInfo}\n\nFile text (raw extracted source document):\n${fileText}`;
        } catch (fileErr) {
          console.error('Failed to fetch source file text for quantity flag', fileErr);
        }
      }
      await navigator.clipboard.writeText(caseInfo);
      setDone(t('adminTesting.review.menuCopied'));
    } catch (err: any) {
      console.error('Failed to copy full fix-quantities prompt', err);
      setError(err.message || 'Failed');
    } finally {
      setBusyItem(null);
    }
  };

  // Copy the Gmail search query and resolve which inbox (63/64) holds the message.
  const handleCopyGmailSearch = async () => {
    if (busy || !flag.gmailId) return;
    setBusyItem('gmail'); setError(null); setDone(null);
    try {
      await navigator.clipboard.writeText(`rfc822msgid:${flag.gmailId}`);
      const account = await findGmailAccount(flag.gmailId).catch(() => null);
      setDone(account
        ? t('adminTesting.review.menuGmailCopiedAccount', { account: account === 'cloud63' ? '63' : '64' })
        : t('adminTesting.review.menuCopied'));
    } catch (err: any) {
      console.error('Failed to copy Gmail search for quantity flag', err);
      setError(err.message || 'Failed');
    } finally {
      setBusyItem(null);
    }
  };

  const handleCodeMonkey = async () => {
    if (busy) return;
    setBusyItem('monkey'); setError(null); setDone(null);
    try {
      let caseInfo = await buildQuantityCaseInfo(flag);
      // Append the raw extracted text of the source invoice that produced this order, so the
      // code-monkey sees what the parser saw. Best-effort: older orders have no stored file.
      if (flag.gmailId) {
        try {
          const { fileText } = await fetchOrderSourceFileText(flag.gmailId, flag.source ?? 'pakbon', flag.setupSource);
          if (fileText) caseInfo = `${caseInfo}\n\nFile text (raw extracted source document):\n${fileText}`;
        } catch (fileErr) {
          console.error('Failed to fetch source file text for quantity flag', fileErr);
        }
      }
      await sendIssueToCodeMonkey({
        flow: 'quantity-issue',
        caseInfo,
        context: {
          ...(flag.setupSource ? { restaurantId: flag.setupSource.restaurantId, setupSource: flag.setupSource } : isAuditTrace ? { traceId: flag.restaurantId } : { restaurantId: flag.restaurantId }),
          restaurantName: flag.restaurantName,
          platform: flag.platform,
          articleId: flag.articleId,
          productName: flag.productName,
          gmailId: flag.gmailId,
          source: flag.source ?? 'pakbon',
        },
      });
      setDone(t('adminTesting.review.menuCodeMonkeySent'));
      setTimeout(close, 1200);
    } catch (err: any) {
      console.error('Failed to send quantity flag to code-monkey', err);
      setError(err.message || 'Failed');
    } finally {
      setBusyItem(null);
    }
  };

  const itemCls = cn('w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-xs transition-colors disabled:opacity-50',
    'hover:bg-gray-100 dark:hover:bg-gray-700', primaryText);

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        title={t('adminTesting.review.rowMenu')}
        className={cn('p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700', secondaryText, open && 'bg-gray-100 dark:bg-gray-700')}
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open && pos && (
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, right: pos.right, zIndex: 50 }}
          className={cn('w-56 p-1.5 rounded-lg border shadow-lg', 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800')}
        >
          <div className="space-y-0.5">
            <button onClick={handleOpenFile} disabled={busy} className={itemCls}>
              <FileText className="w-3.5 h-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
              <span>{busyItem === 'file' ? '…' : t('adminTesting.review.menuOpenFile')}</span>
            </button>
            <button onClick={handleCopyFullPrompt} disabled={busy} className={itemCls}>
              <Copy className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
              <span>{busyItem === 'copy' ? '…' : t('adminTesting.review.menuCopyFullPrompt')}</span>
            </button>
            {flag.gmailId && !flag.setupSource && !flag.gmailId.startsWith('setup-file:') && (
              <button onClick={handleCopyGmailSearch} disabled={busy} className={itemCls}>
                <Search className="w-3.5 h-3.5 shrink-0 text-purple-600 dark:text-purple-400" />
                <span>{busyItem === 'gmail' ? '…' : t('adminTesting.review.menuCopyGmail')}</span>
              </button>
            )}
            <button onClick={handleCodeMonkey} disabled={busy} className={itemCls}>
              <Terminal className="w-3.5 h-3.5 shrink-0 text-teal-600 dark:text-teal-400" />
              <span>{busyItem === 'monkey' ? '…' : t('adminTesting.review.menuCodeMonkey')}</span>
            </button>
            {done && <p className="px-3 py-1 text-[10px] text-green-600 dark:text-green-400">{done}</p>}
            {error && <p className="px-3 py-1 text-[10px] text-red-500 dark:text-textRed-dark">{error}</p>}
          </div>
        </div>
      )}
    </>
  );
}
